import { Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Typography, darken, makeStyles } from '@material-ui/core';
import { Link } from 'react-router-dom';
import HistoryIcon from '@material-ui/icons/History';
import StatSection from './StatSection';
import useTableRules from '../../UI/useTableRules';
import durationToString from '../../../utils/durationToString';
import { ACCEPTED_COLOR, REJECTED_COLOR } from './palette';

const useStyles = makeStyles(theme => ({
    matchRow: {
        '&:hover': {
            backgroundColor: darken(theme.palette.background.paper, 0.2)
        },
    },
    matchCell: {
        display: 'contents',
        '&>a': {
            textDecoration: 'none',
        }
    }
}));

// A match the contestant played, or one of the bracket they have not reached yet
function MatchHistoryCard(props) {
    const rules = useTableRules();
    // Only matches that have actually been started: the bracket carries a node
    // for every match still to be played, and a row of dashes against an opponent
    // who is not known yet says nothing.
    //
    // In the order they were played, which is not the order of the bracket: a
    // loser bracket match can be played before the winner bracket one it follows
    // in the numbering.
    const matches = (props.matches ?? [])
        .filter(match => match.started)
        .sort((lhs, rhs) => lhs.startTime - rhs.startTime);
    return (
        <StatSection
            icon={<HistoryIcon />}
            title='Match History'
            contentProps={{ p: 0 }}
        >
            {matches.length === 0 ?
                <Typography color='textSecondary' style={{ padding: 16 }}> This contestant has not played a match in this tournament yet </Typography> :
                <TableContainer>
                    <Table className={rules.ruled}>
                        <TableHead>
                            <TableRow>
                                <TableCell align='center'> <Typography noWrap> Match ID </Typography> </TableCell>
                                <TableCell align='center'> <Typography noWrap> Contestant </Typography> </TableCell>
                                <TableCell align='center'> <Typography noWrap> Opponent </Typography> </TableCell>
                                <TableCell align='center'> <Typography noWrap> Time </Typography> </TableCell>
                                <TableCell align='center'> <Typography noWrap> Result </Typography> </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {matches.map(match => <MatchRow key={match.matchId} match={match} />)}
                        </TableBody>
                    </Table>
                </TableContainer>
            }
        </StatSection>
    );
}

// The result of a match, e.g. "(W) 2:0" - the two numbers are how many tasks each
// contestant solved, which is not what decides the match: three in a line wins it
// however few tasks that took.
function resultLabel(match) {
    if (match.result == null) return { text: '—', color: 'inherit' };
    if (match.result === 'D') return { text: `(D) ${match.solved}:${match.opponentSolved}`, color: 'inherit' };
    return {
        text: `(${match.result}) ${match.solved}:${match.opponentSolved}`,
        color: match.result === 'W' ? ACCEPTED_COLOR : REJECTED_COLOR
    };
}

function MatchRow(props) {
    const classes = useStyles();
    const { match } = props;
    const result = resultLabel(match);
    return (
        <TableRow className={classes.matchRow}>
            <MatchCell matchId={match.matchId}> {match.label} </MatchCell>
            <MatchCell matchId={match.matchId}> {match.contestant} </MatchCell>
            <MatchCell matchId={match.matchId}> {match.opponent ?? 'TBD'} </MatchCell>
            <MatchCell matchId={match.matchId}> {durationToString(match.length)} </MatchCell>
            <MatchCell matchId={match.matchId} color={result.color}> {result.text} </MatchCell>
        </TableRow>
    );
}

function MatchCell(props) {
    const classes = useStyles();
    return (
        <td className={classes.matchCell}>
            <TableCell align='center' component={Link} to={`/match/${props.matchId}`}>
                <Typography noWrap style={{ color: props.color }}> {props.children} </Typography>
            </TableCell>
        </td>
    );
}

export default MatchHistoryCard;
